import sortBy from 'lodash/sortBy';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import uuid from 'uuid';

import { sendMessage } from 'components/AddMessage/actions';
import { clearItems } from 'components/FCTable/actions';
import config from 'config/config';
import { setGlobalError } from 'containers/ErrorHandler/actions';
import { removeForm } from 'containers/GlobalWrapper/globalStore/forms/actions';
import { selectForm } from 'containers/GlobalWrapper/globalStore/forms/selectors';
import { selectRouteParams } from 'containers/GlobalWrapper/selectors';
import { setGlobalMessage } from 'containers/MessageHandler/actions';
import {
  setLoadingFlag,
  setPhases,
  setPriorities,
  setStatus,
} from 'containers/OneProjectPage/FormsPage/actions';
import {
  ADD_FORM_INSTANCE,
  ADD_TEMPLATE_TO_PROJECT,
  DELETE_FORM_INSTANCE,
  DOWNLOAD_IMPORT_TEMPLATE,
  GET_AVAILABLE_FORMS,
  GET_INFORMATIONS_ABOUT_AVAILABLE_FORMS,
  GET_PHASES,
  GET_PRIORITIES,
  GET_STATUS,
  IMPORT_FORMS,
  NO_PHASE,
  REMOVE_TEMPLATE_TO_PROJECT,
  SET_BULK_PHASE,
  SET_FORM_STATUS,
} from 'containers/OneProjectPage/FormsPage/constants';
import { formsApi } from 'redux/api/forms.api';
import globalMessages from 'translations/messages/global-messages';
import Analytics, { SOURCE } from 'utils/Analytics';
import checkOrSetSlash from 'utils/checkOrSetSlash';
import { uploadMedia } from 'utils/chunkUploader';
import { getFormsApiVersion } from 'utils/constants';
import { getTenantDynamicPropertyId } from 'utils/dynamicProperties';
import { checkParams } from 'utils/errors/check-params';
import { downloadBlob } from 'utils/file';
import { getNameFromContentDisposition, request, requestFile } from 'utils/request';

export function* fetchStatus({ params }) {
  checkParams({ params, keys: ['idData'] });

  const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
    params.idData
  }/statuses`;

  const options = {
    method: 'GET',
    headers: {
      'Cache-Control': 'No-Store',
    },
  };

  const data = yield call(request, requestURL, options);
  if (data && !data.message) {
    yield put(setStatus(sortBy(data.statuses, 'position') || []));
  }
}

export function* getPriorities() {
  const params = yield select(selectRouteParams());
  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${params.idData}/forms-priorities`;

  const options = {
    method: 'GET',
    headers: {
      'Cache-Control': 'No-Store',
    },
  };

  const data = yield call(request, requestURL, options);
  if (data && !data.message) {
    yield put(setPriorities(data.priorities || []));
  }
}

export function* getPhases({ params }) {
  checkParams({ params, keys: ['idData'] });

  const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
    params.idData
  }/phases?with_deleted=false`;

  const options = {
    method: 'GET',
    headers: {
      'Cache-Control': 'No-Store',
    },
  };

  const data = yield call(request, requestURL, options);
  if (data && !data.message) {
    yield put(setPhases(data.phases || []));
  }
}

export function* addTemplateProject({ template }) {
  const route = yield select(selectRouteParams());

  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${route.idData}/attach-forms`;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      form_ids: template,
    }),
  };

  yield call(request, requestURL, options);
  Analytics.track('template_add_project_template');
}
export function* removeTemplateProject({ template }) {
  const route = yield select(selectRouteParams());

  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${route.idData}/remove-forms`;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      form_ids: template,
    }),
  };

  yield call(request, requestURL, options);
}

export function* fetchAvailableForms({ moduleId, callback }) {
  yield put(setLoadingFlag(true));
  const params = yield select(selectRouteParams());

  const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
    params.idData
  }/forms-modules/${moduleId}/forms`;

  const options = {
    method: 'GET',
    headers: {
      'Cache-Control': 'No-Store',
    },
  };

  const data = yield call(request, requestURL, options);
  if (data && !data.message) {
    yield call(callback, data);
  }
  yield put(setLoadingFlag(false));
}

export function* getInformationsAboutAvailableForms({
  formsIds,
  callback,
  searchTerm,
  width_disabled,
}) {
  yield put(setLoadingFlag(true));
  const params = yield select(selectRouteParams());
  const dataUrl = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${
    params.idData
  }/reduced-forms?with_disabled=${width_disabled}`;

  const requestURL = searchTerm ? `${dataUrl}?search_term=${searchTerm}` : dataUrl;
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ form_ids: formsIds }),
  };

  const data = yield call(request, requestURL, options);
  if (data && !data.message) {
    yield call(callback, data);
  }
  yield put(setLoadingFlag(false));
}

export function* addFormInstance({
  formTemplate,
  position,
  moduleId,
  source = 'home',
  isOccupiedSiteRenovation,
  callback,
}) {
  if (!formTemplate) return;
  yield put(setLoadingFlag(true));
  const route = yield select(selectRouteParams());

  let dataForm = {
    id: uuid.v4(),
    name: formTemplate.nameForm,
    form_id: formTemplate.id,
    category_id: formTemplate.categoryId,
    client_created_at: new Date(),
    client_updated_at: new Date(),
  };

  dataForm = {
    ...dataForm,
    module_id: moduleId,
  };

  if (position?.x && position?.y && position?.planId) {
    dataForm.position_x = position.x;
    dataForm.position_y = position.y;
    dataForm.plan_id = position.planId;
  }

  if (formTemplate.group?.group_id) {
    dataForm.group_id = formTemplate.group?.group_id;
  }

  if (isOccupiedSiteRenovation && position?.planId) {
    try {
      const tenantsRequestURL = `${checkOrSetSlash(
        config.apiHostGateway,
        'apiHostGateway',
      )}api/projects/${route.idData}/tenants/plans/${position.planId}`;

      const options = {
        method: 'GET',
        headers: {
          'Cache-Control': 'No-Store',
        },
      };

      // we fetch the tenants for the given plan
      const tenants = yield call(request, tenantsRequestURL, options);
      if (tenants?.length) {
        const firstTenant = tenants[0];

        // we fetch the dynamic properties for the form template
        const dynamicPropertiesRequestURL = `${checkOrSetSlash(
          config.apiHostGatewayGlobal,
          'apiHostGatewayGlobal',
        )}api/organizations/${route.organizationId}/dynamic-properties`;
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([formTemplate.id]),
        };
        const formTemplateDynamicProperties = yield call(
          request,
          dynamicPropertiesRequestURL,
          options,
        );

        // we get the tenant dynamic property id for the form template
        const tenantPropertyId = getTenantDynamicPropertyId(
          formTemplateDynamicProperties,
          isOccupiedSiteRenovation,
        );

        // we assign the tenant to the dynamic property if both are found
        if (tenantPropertyId && firstTenant?.id) {
          dataForm.dynamic_properties_values = {
            ...(dataForm.dynamic_properties_values || {}),
            [tenantPropertyId]: firstTenant.id,
          };
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${route.idData}/form-instances?with_default_answers=true`;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(dataForm),
  };

  const data = yield call(request, requestURL, options);

  if (data && !data.message) {
    if (formTemplate.group?.group_id) {
      const sendMessageRequestOption = {
        method: 'POST',
        url: `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/v1.3/projects/${
          route.idData
        }/groups/${formTemplate.group?.group_id}/messages`,
        dataKey: 'sendFormMessage',
        nameSpace: 'sendFormMessage',
        from: 'Form',
      };
      yield put(
        sendMessage(data.id, 'Form', sendMessageRequestOption, [], () => {
          callback(data);
        }),
      );
    } else {
      yield call(callback, data);
    }
    Analytics.track('forms_creation_succeeded', {
      source,
    });
  }
  yield put(setLoadingFlag(false));
}

export function* deleteFormInstance({ formInstanceId }) {
  yield put(setLoadingFlag(true));
  const route = yield select(selectRouteParams());
  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${route.idData}/form-instances/${formInstanceId}`;

  const options = {
    method: 'DELETE',
  };
  const data = yield call(request, requestURL, options);
  if (data && !data.message) {
    Analytics.track('forms_deleted');
    yield put(clearItems());

    const formData = yield select(selectForm(formInstanceId));
    if (formData) {
      yield put(removeForm(formInstanceId));
    }
    yield put(formsApi.util.invalidateTags(['Categories']));
  }
}

export function* setBulkPhase({ formInstanceIds, phaseId }) {
  const route = yield select(selectRouteParams());

  // Send id = null to backend for "no phase"
  const newPhaseId = phaseId === NO_PHASE ? null : phaseId;

  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${route.idData}/form-instances/bulk-phase-update`;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      new_phase_id: newPhaseId,
      ids: formInstanceIds,
    }),
  };

  yield call(request, requestURL, options);
  Analytics.track('forms_engagement');
  Analytics.track('forms_phase_changed', { source: SOURCE.massEdit });
  yield put(clearItems());
}

export function* importForms({ item, params, callback }) {
  const requestUploadMediaUrl = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/v1.2/projects/${params.idData}/`;
  const dataUpload = yield call(uploadMedia, item, {
    baseUrl: requestUploadMediaUrl,
    parallel: false,
    requestHeaders: {
      'Content-Type': 'application/json',
    },
  });

  if (!dataUpload?.id) {
    yield put(
      setGlobalError({
        customErrorMessageIntl: globalMessages.error_in_upload_import,
      }),
    );
    return;
  }

  const payload = {
    media_id: dataUpload.id,
  };

  const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
    params.idData
  }/import/forms/module/${params.itemId}`;

  const optionsUpload = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  };
  const data = yield call(request, requestURL, optionsUpload, {
    errorRedirect: false,
    errorCustom: true,
  });
  if (data && (data.message || data.status === 500)) {
    if (data.status === 500) {
      yield put(
        setGlobalError({
          data,
          customErrorMessageIntl: globalMessages.masse_import_wrong_template,
        }),
      );
    } else if (data?.response?.code === 'INCORRECTLY_FILLED_TEMPLATE') {
      yield put(
        setGlobalError({
          data,
          customErrorMessageIntl: globalMessages.masse_import_error_filled,
        }),
      );
    } else if (data?.response?.code === 'WRONG_PROJECT_TEMPLATE') {
      yield put(
        setGlobalError({
          data,
          customErrorMessageIntl: globalMessages.masse_import_wrong_project_template,
        }),
      );
    } else if (data?.response?.code === 'WRONG_MODULE_TEMPLATE') {
      yield put(
        setGlobalError({
          data,
          customErrorMessageIntl: globalMessages.masse_import_wrong_module_template,
        }),
      );
    } else if (data?.response?.code === 'EMPTY_TEMPLATE') {
      yield put(
        setGlobalError({
          data,
          customErrorMessageIntl: globalMessages.imported_template_is_empty,
        }),
      );
    }
  } else {
    yield put(
      setGlobalMessage({
        type: 'success',
        intlMessage: {
          id: 'masse_import_form_started_description',
        },
        active: true,
      }),
    );
  }

  if (callback) {
    yield call(callback);
  }
}

export function* downloadImportTemplate({ params }) {
  const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
    params.idData
  }/import/template/forms/module/${params.itemId}`;

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  };
  yield put(
    setGlobalMessage({
      type: 'success',
      intlMessage: {
        id: 'download_in_progress',
      },
      active: true,
    }),
  );

  const data = yield call(requestFile, requestURL, options);
  if (data && !data.message) {
    const name = getNameFromContentDisposition(data.headers);
    const reader = data.body?.getReader();
    const result = [];
    if (!reader) return;
    reader.read().then(function processText({ done, value }) {
      if (done) {
        downloadBlob(result, name || `template.xlsx`);
        return;
      }
      result.push(value);
      reader.read().then(processText);
    });
  }
}

export function buildItemFetchPayLoadByOffset(
  projectId,
  moduleId,
  search,
  filters = {},
  excludedIds = [],
) {
  return ({ pageSize, offset }) => {
    const assigneeIds = Array.from(
      new Set([...(filters?.assignee_ids || []), ...(filters?.assigned_to_me || [])]),
    );

    return {
      method: 'POST',
      path: `api/projects/${projectId}/form-instances/filter`,
      rootKey: 'form_instances',
      body: {
        ...filters,
        assignee_ids: assigneeIds,
        module_ids: [moduleId],
        excluded_form_instance_ids: excludedIds,
        ...(search ? { search_term: search } : {}),
      },
      params: {
        limit: pageSize,
        offset,
      },
      totalCountKey: 'count',
    };
  };
}

export function buildItemFetchPayLoad(projectId, moduleId, search, filters = {}, excludedIds = []) {
  return ({ pageIndex, pageSize }) =>
    buildItemFetchPayLoadByOffset(projectId, moduleId, search, filters, excludedIds)({
      pageSize,
      offset: pageIndex * pageSize,
    });
}

export function* setFormStatus({ idData, optionId, statusId }) {
  const requestURL = `${checkOrSetSlash(
    config.apiHostGateway,
    'apiHostGateway',
  )}api/${getFormsApiVersion()}/projects/${idData}/form-instances/${optionId}/set-status`;

  const options = {
    method: 'PUT',
    headers: { 'Cache-Control': 'No-Store', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      status_id: statusId,
    }),
  };

  yield call(request, requestURL, options);

  Analytics.track('forms_status_changed');
  yield put(clearItems());
}

export default function* initFormsPageSaga() {
  yield takeLatest(SET_FORM_STATUS, setFormStatus);
  yield takeLatest(GET_STATUS, fetchStatus);
  yield takeLatest(GET_PRIORITIES, getPriorities);
  yield takeLatest(ADD_TEMPLATE_TO_PROJECT, addTemplateProject);
  yield takeLatest(REMOVE_TEMPLATE_TO_PROJECT, removeTemplateProject);
  yield takeLatest(GET_AVAILABLE_FORMS, fetchAvailableForms);
  yield takeEvery(GET_INFORMATIONS_ABOUT_AVAILABLE_FORMS, getInformationsAboutAvailableForms);
  yield takeLatest(ADD_FORM_INSTANCE, addFormInstance);
  yield takeLatest(DELETE_FORM_INSTANCE, deleteFormInstance);
  yield takeLatest(GET_PHASES, getPhases);
  yield takeLatest(SET_BULK_PHASE, setBulkPhase);
  yield takeLatest(IMPORT_FORMS, importForms);
  yield takeLatest(DOWNLOAD_IMPORT_TEMPLATE, downloadImportTemplate);
}
